import {useCallback, useEffect, useRef, useState} from 'react'
import {Text, View} from 'react-native'
import {useNavigationState} from '@react-navigation/native'

import {getCurrentRoute} from '#/lib/routes/helpers'
import {GrapeIcon} from '#/view/icons/GrapeIcon'
import {atoms as a, flatten} from '#/alf'

const DEFAULT_FILL = '#C22EFF'
const GLINT = '#F0C8FF'
const DENSITY = ['.', ':', 'o', 'O', '@'] as const
const CRYSTAL = ['.', '+', '*', '◆', '◇', '*', '+'] as const

const ASCII_GRAPE_MASK = [
  '         #  ',
  '        #   ',
  '   ##  ##   ',
  '  ##  ##  # ',
  ' ##  ##  ## ',
  '  ##  ##  # ',
  '   ##  ##   ',
  '    ##      ',
]

/** One solid → ascii → solid crystalline cycle. */
const CYCLE_MS = 4600
const FACETS = 9
const DEFAULT_INTERVAL_MS = 60_000

/**
 * Lucide grape that plays the splash-style crystalline morph on mount,
 * on navigation changes, and on a long interval (default 1 min).
 */
export function MorphingGrapeMark({
  size = 30,
  fill = DEFAULT_FILL,
  intervalMs = DEFAULT_INTERVAL_MS,
}: {
  size?: number
  fill?: string
  intervalMs?: number
}) {
  const [playing, setPlaying] = useState(false)
  const [cycleStart, setCycleStart] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [densityPhase, setDensityPhase] = useState(0)
  const [reduceMotion, setReduceMotion] = useState(false)
  const playLock = useRef(false)
  const reduceMotionRef = useRef(false)

  const routeName = useNavigationState(state =>
    state ? getCurrentRoute(state).name : 'Home',
  )

  const play = useCallback(() => {
    if (reduceMotionRef.current || playLock.current) return
    playLock.current = true
    setCycleStart(Date.now())
    setElapsed(0)
    setPlaying(true)
  }, [])

  useEffect(() => {
    reduceMotionRef.current = reduceMotion
  }, [reduceMotion])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduceMotion(mq.matches)
    const onChange = () => setReduceMotion(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Mount + route change
  useEffect(() => {
    if (reduceMotion) return
    play()
  }, [routeName, reduceMotion, play])

  // Periodic replay
  useEffect(() => {
    if (reduceMotion) return
    if (typeof window === 'undefined') return
    const id = window.setInterval(() => {
      playLock.current = false
      play()
    }, intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs, reduceMotion, play])

  useEffect(() => {
    if (!playing || reduceMotion) return
    let raf = 0
    const loop = () => {
      const next = Date.now() - cycleStart
      if (next >= CYCLE_MS) {
        setElapsed(CYCLE_MS)
        setPlaying(false)
        playLock.current = false
        return
      }
      setElapsed(next)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [playing, cycleStart, reduceMotion])

  useEffect(() => {
    if (!playing || reduceMotion) return
    if (typeof window === 'undefined') return
    const id = window.setInterval(() => {
      setDensityPhase(p => (p + 1) % DENSITY.length)
    }, 120)
    return () => window.clearInterval(id)
  }, [playing, reduceMotion])

  const morph = morphState(playing ? elapsed : 0, !playing || reduceMotion)
  const fontSize = Math.max(4, Math.round(size * 0.12))
  const lineHeight = Math.max(5, Math.round(fontSize * 1.15))

  return (
    <View
      style={[
        {
          width: size,
          height: size,
          position: 'relative',
          transform: [{scale: morph.scale}, {rotate: `${morph.rotateDeg}deg`}],
        },
      ]}
      accessibilityRole="image"
      accessibilityLabel="GrapeVyne"
      accessibilityHint="">
      <View
        style={[
          a.absolute,
          a.inset_0,
          a.align_center,
          a.justify_center,
          {opacity: morph.solid},
        ]}>
        <GrapeIcon width={size} fill={fill} />
      </View>
      <View
        style={[
          a.absolute,
          a.inset_0,
          a.align_center,
          a.justify_center,
          {opacity: morph.glint},
        ]}
        pointerEvents="none">
        <GrapeIcon width={size} fill={GLINT} />
      </View>
      <View
        style={[
          a.absolute,
          a.inset_0,
          a.align_center,
          a.justify_center,
          {opacity: morph.ascii},
        ]}
        pointerEvents="none">
        <AsciiMark
          densityPhase={densityPhase}
          reveal={morph.facetReveal}
          crystalline={morph.inTransition}
          dissolveOut={morph.dissolveOut}
          color={fill}
          fontSize={fontSize}
          lineHeight={lineHeight}
        />
      </View>
    </View>
  )
}

function AsciiMark({
  densityPhase,
  reveal,
  crystalline,
  dissolveOut = false,
  color,
  fontSize,
  lineHeight,
}: {
  densityPhase: number
  reveal: number
  crystalline: boolean
  dissolveOut?: boolean
  color: string
  fontSize: number
  lineHeight: number
}) {
  const charset = crystalline ? CRYSTAL : DENSITY

  return (
    <View style={[a.align_center, a.justify_center]}>
      {ASCII_GRAPE_MASK.map((row, rowIndex) => (
        <Text
          key={rowIndex}
          style={flatten([
            {
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize,
              lineHeight,
              letterSpacing: fontSize > 6 ? 0.5 : 0,
              color: crystalline ? GLINT : color,
            },
          ])}>
          {row
            .split('')
            .map((c, colIndex) => {
              if (c === ' ') return ' '
              const thresh = facetThreshold(rowIndex, colIndex)
              const visible = dissolveOut ? reveal < thresh : reveal >= thresh
              if (!visible) return ' '
              const i =
                (rowIndex * 3 + colIndex + densityPhase) % charset.length
              return charset[i]
            })
            .join('')}
        </Text>
      ))}
    </View>
  )
}

type MorphState = {
  solid: number
  ascii: number
  glint: number
  scale: number
  rotateDeg: number
  facetReveal: number
  inTransition: boolean
  dissolveOut: boolean
}

function morphState(elapsed: number, freezeSolid: boolean): MorphState {
  if (freezeSolid) {
    return {
      solid: 1,
      ascii: 0,
      glint: 0,
      scale: 1,
      rotateDeg: 0,
      facetReveal: 0,
      inTransition: false,
      dissolveOut: false,
    }
  }

  const t = Math.min(1, elapsed / CYCLE_MS)

  if (t < 0.26) {
    return baseMorph({solid: 1, ascii: 0})
  }
  if (t < 0.44) {
    const raw = (t - 0.26) / 0.18
    const p = facet(easeInOut(raw))
    const glint = crystalGlint(raw)
    return {
      solid: 1 - p * 0.95,
      ascii: Math.min(1, p * 1.15),
      glint,
      scale: 1 + glint * 0.06,
      rotateDeg: (raw - 0.5) * 3,
      facetReveal: p,
      inTransition: true,
      dissolveOut: false,
    }
  }
  if (t < 0.68) {
    return baseMorph({solid: 0, ascii: 1})
  }
  if (t < 0.86) {
    const raw = (t - 0.68) / 0.18
    const p = facet(easeInOut(raw))
    const glint = crystalGlint(raw)
    return {
      solid: Math.min(1, p * 1.15),
      ascii: 1 - p * 0.95,
      glint,
      scale: 1 + glint * 0.06,
      rotateDeg: (0.5 - raw) * 3,
      facetReveal: 1 - p,
      inTransition: true,
      dissolveOut: true,
    }
  }
  return baseMorph({solid: 1, ascii: 0})
}

function baseMorph({solid, ascii}: {solid: number; ascii: number}): MorphState {
  return {
    solid,
    ascii,
    glint: 0,
    scale: 1,
    rotateDeg: 0,
    facetReveal: ascii,
    inTransition: false,
    dissolveOut: false,
  }
}

function facet(x: number) {
  const clamped = Math.min(1, Math.max(0, x))
  return Math.round(clamped * FACETS) / FACETS
}

function crystalGlint(raw: number) {
  const bell = Math.sin(Math.min(1, Math.max(0, raw)) * Math.PI)
  return facet(bell * bell)
}

function facetThreshold(row: number, col: number) {
  return ((row * 5 + col * 7 + (row ^ col) * 3) % (FACETS + 1)) / FACETS
}

function easeInOut(x: number) {
  const clamped = Math.min(1, Math.max(0, x))
  return clamped * clamped * (3 - 2 * clamped)
}
